import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import debounce from 'lodash-es/debounce';
import max from 'lodash-es/max';
import find from 'lodash-es/find';
import Flickity from 'react-flickity-component';
import 'flickity-imagesloaded';

import ProductTile from '../React/ProductTile.jsx';
import AdMindClick from '../AdMind/click';
import {
  getGraphIDFromProductId,
  getShopifyProductsForCountry,
} from '../../utils/retrieveShopifyProductsForSelectedCountry';

const handleResize = debounce((carousel) => {
  if (!carousel) {
    return;
  }

  try {
    carousel.current.flkty.resize();
  } catch (error) {
    console.log('error:', error);
  }
}, 250);

const maxRetries = 10;
let fallBackInterval;
let retryCount = 0;

const TrendingCarousel = (props) => {
  const carousel = useRef(null);
  const [items, setItems] = useState([]);
  const [title, setTitle] = useState('');

  const handleLoad = (event) => {
    setItems(event.detail.monetateData);
    setTitle(event.detail.title);
    if (event.detail.title) {
      const carouselHeadings = document.querySelectorAll('.ProductRecommendationsCarousel__heading');
      if (carouselHeadings.length > 0) {
        carouselHeadings[props.index].innerHTML = event.detail.title;
      }
    }
  };
  useEffect(() => {
    document.addEventListener(`monetateLoaded${props.scheme}`, handleLoad);
    return () => {
      document.removeEventListener(`monetateLoaded${props.scheme}`, handleLoad);
    };
  }, []);

  const getFirstVariantImageWithColor = (data, color) => {
    let img = null;

    for (let i = 0; i < data.Child_Attributes.length; i++) {
      const variant = data.Child_Attributes[i];

      if (variant.option1 === color) {
        img = variant.image_url.split('?')[0].replace('.jpg', '_600x.jpg').replace('.webp', '_600x.webp');
        break;
      }
    }

    return img;
  };

  const getFirstVariantBarcodeWithColor = (data, color) => {
    let barcode = null;

    for (let i = 0; i < data.Child_Attributes.length; i++) {
      const variant = data.Child_Attributes[i];

      if (variant.option1 === color) {
        barcode = variant.barcode;
        break;
      }
    }

    return barcode;
  };

  const getFirstAvailableVariant = (product) => {
    try {
      for (let i = 0; i < product.Child_Attributes.length; i++) {
        const variant = product.Child_Attributes[i];

        if (variant && variant.inventory && Number(variant.inventory) > 0) {
          return variant;
        }
      }
    } catch (error) {
      console.log('error - getFirstAvailableVariant:', error);
      return product.Child_Attributes[0];
    }
  };

  const getOptions = (data) => {
    const imgs = data.color.map((color) => {
      return getFirstVariantImageWithColor(data, color);
    });

    const Barcode = data.color.map((color) => {
      return getFirstVariantBarcodeWithColor(data, color);
    });

    const options = {
      Color: data.color,
      Size: data.size,
      imgs,
      Barcode,
    };

    return options;
  };

  const mapVariants = (variants) => {
    return variants.map((variant) => {
      return {
        ...variant,
        Sellable: Number(variant.inventory) > 0,
        imgs: variant.image_url,
        Color: variant.option1,
        id: variant.variant_id,
        'Product-sku': variant.sku ? variant.sku : '',
      };
    });
  };

  const carouselOptions = {
    imagesLoaded: true,
    cellAlign: 'center',
    contain: true,
    groupCells: true,
    draggable: true,
    dragThreshold: 10,
    wrapAround: true,
    on: {
      staticClick: (e, pointer, cellElement, cellIndex) => {
        const item = items[cellIndex];
        const firstVariant = item.Child_Attributes[0];
        const comparePrices = item.Child_Attributes.map((variant) => {
          return variant.compare_at_price;
        });
        const comparePriceMax = max(comparePrices);

        let listName = false;
        const currentTarget = cellElement;
        const collectionName = currentTarget.closest('[data-collection]');

        if (collectionName) {
          listName = collectionName.dataset.collection;
          window.sessionStorage.setItem('rec_list_name', listName);

          if (pointer.target.tagName === 'IMG') {
            window.dataLayer.push({
              event: 'recommendation_click',
              product_name: item.Title,
              product_id: item.item_group_id,
              recommendation_item_clicked: item.item_group_id,
              page_type: window.ShopifyAnalytics.meta.page.pageType,
            });
            console.log('recommendation_click', item.Title, item.item_group_id);
          } else {
            window.dataLayer.push({
              event: 'quick_shop_click',
              product_name: item.Title,
              product_id: item.item_group_id,
              product_to_quick_shop: item.product_id,
              page_type: window.ShopifyAnalytics.meta.page.pageType,
            });
            console.log('quick_shop_click', item.Title, item.item_group_id);
          }

          const product = {
            id: item.item_group_id,
            title: item.Title,
            product_type: item.product_type,
            price: firstVariant.price,
            price_compare: firstVariant.compare_at_price,
            price_compare_max: comparePriceMax,
            tags: item.tags.split(','),
            variants: item.Child_Attributes,
            list_name: listName,
          };

          AdMindClick(product);
        }
      },
    },
  };

  return (
    <div>
      <div className="TrendingCarousel__heading">
        {title && (
          <h1 role="heading" aria-level="2">
            {title}
          </h1>
        )}
      </div>
      <div data-index={props.index}>
        {items && items.length > 0 && (
          <Flickity
            className="TrendingCarouselComponent"
            options={carouselOptions}
            reloadOnUpdate={true}
            ref={carousel}
          >
            {items.map((item, i) => {
              // Map the item options to match the Instant Search format
              const options = getOptions(item);

              if (!item.Child_Attributes) {
                return null;
              }

              const firstAvailableVariant = getFirstAvailableVariant(item);
              const comparePrices = item.Child_Attributes.map((variant) => {
                return variant.compare_at_price;
              });
              const comparePriceMax = max(comparePrices);

              const productUrl = item.Detail_URL.replace('https://windsor-us.myshopify.com', '');
              let urlParams = null;
              if (productUrl) {
                try {
                  const url = new URL(productUrl);
                  if (url.search) {
                    urlParams = new URLSearchParams(url.search);
                  }
                } catch (error) {
                  console.error('Error constructing URL:', error);
                }
              }
              let recToken = null;
              if (urlParams) {
                recToken = urlParams.get('recToken');
              }
              return (
                <ProductTile
                  key={`TrendingCarousel__ProductTile-${item.ID}-${i}`}
                  variant={firstAvailableVariant.variant_id}
                  id={item.product_id}
                  pid={item.ID}
                  title={item.Title}
                  product_url={productUrl}
                  product_type={item.product_type}
                  thumb_url={firstAvailableVariant.image_url}
                  thumb_alt={item.Title}
                  price={firstAvailableVariant.price}
                  price_compare={firstAvailableVariant.compare_at_price}
                  price_compare_max={comparePriceMax}
                  tags={item.tags.split(',')}
                  variants={mapVariants(item.Child_Attributes)}
                  options={options}
                  isRec={true}
                  shouldReportClickToAdmind={true}
                  selectedColor={item.selectedColor}
                  recToken={recToken}
                  index={i}
                />
              );
            })}
          </Flickity>
        )}
      </div>
    </div>
  );
};

TrendingCarousel.defaultProps = {
  items: [],
};

const renderTrendingCarousel = async (el) => {
  const scheme = el.getAttribute('data-scheme');
  const items =
    window.certonaSchemes && window.certonaSchemes[scheme]
      ? window.certonaSchemes[scheme]
      : window.certonaSchemes[Object.keys(window.certonaSchemes)[0]];

  const graphProductIds = items.map((item) => {
    return getGraphIDFromProductId(item.product_id);
  });

  const shopify_products = await getShopifyProductsForCountry(graphProductIds);

  const final_items_list = [];
  for (const product of shopify_products) {
    if (!product.variants) {
      console.error(`No variants found in Shopify for this product: ${product.id}`);
      return;
    }
    const product_id_parts = product.id.split('/');
    const product_id = product_id_parts[product_id_parts.length - 1];
    const certona_product = find(items, {
      product_id,
    });
    if (!certona_product) {
      return;
    }
    for (const variant of product.variants) {
      const variant_id_parts = variant.id.split('/');
      const variant_id = variant_id_parts[variant_id_parts.length - 1];
      const certona_variant = find(certona_product.Child_Attributes, {
        variant_id,
      });
      if (!certona_variant) {
        return;
      }
      certona_variant.compare_at_price = Number(variant.compareAtPrice.amount).toFixed(2);
      certona_variant.price = Number(variant.price.amount).toFixed(2);
    }
    final_items_list.push(certona_product);
  }
  if (final_items_list.length === 0) {
    $('.TrendingCarousel').hide();
  }
  ReactDOM.render(<TrendingCarousel items={final_items_list} />, el);
};

export default () => {
  document.querySelectorAll('[data-trending-carousel]').forEach((el, i) => {
    if (el.getAttribute('initialized') === 'true') {
      return;
    }

    const scheme = el.getAttribute('data-scheme');

    const cellAlign = el.getAttribute('data-cell-align');
    let productPage = false;

    ReactDOM.render(
      <TrendingCarousel scheme={scheme} cellAlign={cellAlign} index={i} isProductPage={productPage} />,
      el,
    );

    // Attached initialized when certona loads
    el.setAttribute('initialized', 'true');
  });
};
